import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useCustomerExperienceFeedback = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const getCustomerFeedback = useCallback(
    async ({ status = null, entertainmentId = null, branchId = null, page = 1, limit = 50 } = {}) => {
      try {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (entertainmentId) params.append('entertainmentId', entertainmentId);
        if (branchId) params.append('branchId', branchId);
        params.append('page', String(page));
        params.append('limit', String(limit));

        return await sendRequest({
          url: `/api/v1/experienceFeedback?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden des Kundenfeedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getFeedbackById = useCallback(
    async (feedbackId) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden des Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateStatus = useCallback(
    async ({ feedbackId, status }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/status`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { status },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  /**
   * Marks the customer as called and optionally saves a call note.
   */
  const updateCustomerCalled = useCallback(
    async ({ feedbackId, customerCalled, callNote = null }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/customerCalled`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { customerCalled, callNote },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Anruf-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const generateAiSuggestion = useCallback(
    async ({ feedbackId }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/aiSuggestion`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        });
      } catch (err) {
        toast.error('Fehler beim Generieren des KI-Vorschlags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const sendResponse = useCallback(
    async ({ feedbackId, responseText, courtesyVoucherValue }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/respond`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { responseText, courtesyVoucherValue },
        });
      } catch (err) {
        toast.error('Fehler beim Senden der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    getCustomerFeedback,
    getFeedbackById,
    updateStatus,
    updateCustomerCalled,
    generateAiSuggestion,
    sendResponse,
  };
};

export default useCustomerExperienceFeedback;
